import React, { useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import ResolveImage from 'components/ResolveImage';
import { Listing } from 'models/Listing';
import utils, {
  checkIsTierPremium,
  getIsListingOwner,
  getPortfolioRoute,
  getResolveUrl,
  isNewListing,
} from 'utils';
import { Link } from 'react-router-dom';
import ResolvePopup from 'components/ResolvePopup';
import { Button } from 'ncoded-component-library';
import StarIcon from 'icons/Star.icon';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import AuthContext from 'providers/Auth/Auth.context';
import EoiDate from 'components/EoiDate';
import LocationmarkerIcon from 'icons/Locationmarker.icon';
import StatusBadge from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/StatusBadge';
import TextBubleIcon from 'icons/TextBuble.icon';
import LinkToIcon from 'icons/LinkTo.icon';
import ShareButton from 'components/ShareButton';
import { getImgUrl } from 'components/ResolveImage/utils';
import useMatchMedia from 'hooks/useMatchMedia';
import useShowEnquireAndWatchlist from 'hooks/useShowEnquireAndWatchlist';
import useWatchlist from 'hooks/useWatchlist';

import './DistressedListingCard.styles.scss';
import './DistressedListingCard.styles.responsive.scss';

type DistressedListingCardProps = {
  className?: string;
  listing: Listing;
  onWatchedListed: (value: boolean, listingId: string) => void;
  onEnquire: (listingId: string, redirectLink?: string) => void;
};

const DistressedListingCard: React.FC<DistressedListingCardProps> = (props) => {
  const { className, listing, onWatchedListed, onEnquire } = props;
  const { isLoggedIn } = useContext(AuthContext);
  const { t } = useTranslation();
  const isPhablet = useMatchMedia('isPhablet');

  const { currentUser } = useContext(CurrentUserContext);
  const currentUserId = currentUser?._id || '';

  const {
    _id: listingId,
    listingTitle,
    isWatchlisted,
    industry,
    gallery,
    userId,
    locationPostcode,
    createdAt,
    slugName,
    businessDescription,
    type,
    listingContact,
    isEnquired,
    listingEnquiryId,
    dealRoomId,
    seekingExpressionOfInterestDate,
    isCoOwner,
    tier,
  } = listing;

  const firmLogo = listingContact?.firmLogo?.url;

  const isListingPremium = checkIsTierPremium(tier);

  const isListingOwner = getIsListingOwner(currentUserId, userId, isCoOwner);

  const classes = classNames('urgent-campaigns-listing-card', className);

  const redirectionListingLink = utils.composeBusinessUrl(
    industry,
    slugName,
    type,
  );

  const { showEnquire } = useShowEnquireAndWatchlist(listing);

  const { loading: watchlistRequestLoading, onWatchlist } = useWatchlist(
    listingId,
    dealRoomId,
    isWatchlisted,
    onWatchedListed,
  );

  return (
    <div className={classes}>
      <div className="urgent-campaigns-listing-card__left">
        <div className="urgent-campaigns-listing-card__left__heading">
          <StatusBadge isNew={isNewListing(createdAt)} />
          <Link
            className="urgent-campaigns-listing-card__left__heading__title"
            to={redirectionListingLink}
          >
            {listingTitle}
          </Link>
          <Link
            to={redirectionListingLink}
            className="urgent-campaigns-listing-card__left__heading__listing-type"
          >
            <span>
              <LocationmarkerIcon />
              {locationPostcode}
            </span>
            <span>{industry}</span>
          </Link>
        </div>
        <EoiDate
          seekingExpressionOfInterestDate={seekingExpressionOfInterestDate}
        />
        <Link
          className="urgent-campaigns-listing-card__left__description"
          to={redirectionListingLink}
        >
          {businessDescription}
        </Link>
        <div className="urgent-campaigns-listing-card__left__actions">
          {showEnquire && (
            <Button
              variant="outline"
              onClick={() => onEnquire(listingId, redirectionListingLink)}
              icon={TextBubleIcon}
            >
              {t('enquire')}
            </Button>
          )}
          {(isListingOwner || isEnquired) && (
            <Link
              className="as-button ncoded-button--link"
              to={getPortfolioRoute(
                !isListingOwner,
                dealRoomId,
                listingEnquiryId,
              )}
            >
              <LinkToIcon />
              {t(isPhablet ? 'viewPortfolio' : 'viewInMyPortfolio')}
            </Link>
          )}
          <ResolvePopup
            onHover
            popupContent={t(
              isListingOwner
                ? 'thisIsYourListing'
                : isLoggedIn
                ? isWatchlisted
                  ? 'removeWatchlistedListing'
                  : 'watchlistListing'
                : 'loginToWatchlist',
            )}
          >
            <Button
              className={classNames('watchlist-button', {
                'watch-listed': isWatchlisted,
              })}
              variant="link"
              icon={<StarIcon />}
              disabled={isListingOwner || watchlistRequestLoading}
              onClick={onWatchlist}
            >
              {t('shortlist')}
            </Button>
          </ResolvePopup>
          <ShareButton url={getResolveUrl(redirectionListingLink)} />
        </div>
      </div>

      <Link
        aria-label="Business details page"
        className="urgent-campaigns-listing-card__gallery-img"
        to={redirectionListingLink}
      >
        <ResolveImage
          src={getImgUrl(gallery[0]?.url, '320')}
          alt="Listing gallery."
          shouldLazyLoad={false}
        />
        {isListingPremium && firmLogo && (
          <ResolveImage
            asCompanyLogo
            alt="Company logo."
            src={getImgUrl(firmLogo, '240')}
            shouldLazyLoad={false}
          />
        )}
      </Link>
    </div>
  );
};

export default DistressedListingCard;
